import { useContext, useMemo } from 'react'
import usePublishSalesCopilotStay from 'components/OfferPage/SalesCopilot/usePublishSalesCopilotStay'
import CurrencyContext from 'contexts/currencyContext'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import useBedbankRates from 'hooks/useBedbankRates'
import { min } from 'lib/array/arrayUtils'
import { getIsFlightsSelected } from 'selectors/offerPage/offerPageSelectors'

interface Props {
  offer: App.BedbankOffer
}

// Publishes the cheapest rate for the agent's dates on bedbank offer pages, the same price the summary shows.
function SalesCopilotBedbankBridge({ offer }: Props) {
  const offerPageState = useContext(OfferPageStateContext)
  const currentCurrency = useContext(CurrencyContext)
  const { checkInDate, checkOutDate, rooms, airport } = offerPageState
  const [{ hotelOnlyRates, hotelBundleRates }] = useBedbankRates(offer.id, rooms, checkInDate, checkOutDate)
  const flightPrice = useBedbankFlightPrice(offer, { rooms, startDate: checkInDate, endDate: checkOutDate, originAirportCode: airport?.code })
  const flightsSelected = getIsFlightsSelected(offerPageState)

  const offerStay = useMemo<App.SalesCopilotOfferStay | undefined>(() => {
    const rate = min(hotelOnlyRates, rate => rate.totals.inclusive)
    if (!checkInDate || !checkOutDate || !rate) return undefined

    const bundleRate = min(hotelBundleRates, rate => rate.totals.inclusive)
    const hotelTotal = rate.totals.inclusive
    const total = flightsSelected && bundleRate && flightPrice ? bundleRate.totals.inclusive + flightPrice : hotelTotal

    return {
      offerId: offer.id,
      offerType: offer.type,
      offerName: offer.name,
      checkIn: checkInDate,
      checkOut: checkOutDate,
      nights: rate.nights,
      currencyCode: currentCurrency,
      total,
      hotelTotal,
      hotelMargin: rate.marginAud,
      flightsSelected,
      // Bedbank stays take a payment schedule rather than a deposit, which checkout lists.
      depositAvailable: false,
      depositPercentage: 0,
      depositDueDays: 0,
      reserveForZeroAvailable: rate.isReserveForZeroEligible === true,
      availableRooms: rate.availableRooms,
      bookedLast72Hours: offer.stats?.booked,
      valuedAt: rate.value,
      discountPercent: rate.discount,
    }
  }, [offer, checkInDate, checkOutDate, hotelOnlyRates, hotelBundleRates, flightsSelected, flightPrice, currentCurrency])

  usePublishSalesCopilotStay(offerStay)
  return null
}

export default SalesCopilotBedbankBridge
